import { validateOpeningSet } from '../../domain/openings/validateOpeningSet';
import * as THREE from "three";
import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type { RoofConfig, SaunaDimensions } from "../../engines/types";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import { emitter } from "../../lib/eventBus";

export type OpeningWallId = "front" | "back" | "left" | "right" | "divider";

interface WallPlaneConfig {
	id: OpeningWallId;
	plane: THREE.Mesh;
	lengthMm: number;
	toWorld: (xMm: number, yMm: number) => THREE.Vector3;
	fromWorld: (point: THREE.Vector3) => { xMm: number; yMm: number };
}

interface OpeningDragConfig {
	scene: THREE.Scene;
	/**
	 * Object the pick planes and the ghost are parented to. The viewer passes
	 * the rotated sauna rig, which is what makes this controller correct at a
	 * rotated orientation: the planes are built in MODEL space and inherit the
	 * rotation, and `hit.point` is converted back through the same node. Falls
	 * back to the scene root (identity transform) when absent.
	 */
	parent?: THREE.Object3D;
	camera: THREE.PerspectiveCamera;
	renderer: THREE.WebGLRenderer;
	dimensions: SaunaDimensions;
	existingOpenings: WallOpening[];
	overrides?: RuleOverrides;
	partitionSplit?: number;
 hasRelaxRoom?: boolean;
 saunaPosition?: "left" | "right";
 roofConfig?: RoofConfig;
	/** The design's wall height (issue 155); absent = the rule default. */
	wallHeightM?: number;
	onCommit: (opening: WallOpening) => void;
	onCancel?: () => void;
	onDragUpdate?: (valid: boolean, wall: OpeningWallId | null) => void;
}

const WALL_BASE_Y = 0.165;
const DIVIDER_EDGE_INSET_MM = 95;

const MAT_VALID = new THREE.MeshBasicMaterial({
	color: 0x22c55e,
	transparent: true,
	opacity: 0.45,
	depthTest: false,
});

function nextOpeningId(): string {
	if ("randomUUID" in crypto) return crypto.randomUUID();
	return `opening-${Date.now()}`;
}

function getDividerZ(length: number, partitionSplit = 0.5) {
	return -length / 2 + length * partitionSplit;
}

function getDividerLengthMm(dimensions: SaunaDimensions) {
	return Math.max(
		0,
		Math.round(dimensions.width * 1000),
	);
}

export class OpeningDragController {
	private parent: THREE.Object3D;
	private camera: THREE.PerspectiveCamera;
	private domElement: HTMLElement;
	private dimensions: SaunaDimensions;
	private existingOpenings: WallOpening[];
	private overrides: RuleOverrides;
	private partitionSplit: number;
 private validationConfig: Pick<OpeningDragConfig, "partitionSplit" | "hasRelaxRoom" | "saunaPosition" | "roofConfig">;
	private onCommit: (opening: WallOpening) => void;
	private onCancel?: () => void;
	private onDragUpdate?: (valid: boolean, wall: OpeningWallId | null) => void;

	private raycaster = new THREE.Raycaster();
	private mouse = new THREE.Vector2();
	private wallPlanes: WallPlaneConfig[] = [];
	private ghostMesh: THREE.Mesh | null = null;
	private ghostGroup: THREE.Group | null = null;
	private isDragging = false;
	private activeOpening: WallOpening | null = null;
	private minCornerClearanceMm: number;
	private wallHeightMm: number;
	private allowedWalls: OpeningWallId[] = [];

	private currentWall: OpeningWallId | null = null;
	private currentValid = false;
	private currentXMm = 0;
	private currentYMm = 0;
	/**
	 * `place` = the user has just clicked "Add window" and is now
	 * hovering over the model. The pointer button is *not* held.
	 * `move` = the user grabbed an existing opening handle. The
	 * pointer button is held throughout. Behaviour differs:
	 * place mode lets orbit run alongside (so the user can rotate
	 * the model while picking a wall) and only commits on a real
	 * click; move mode treats every pointermove as an active drag.
	 */
	private mode: "place" | "move" = "place";
	private pointerDownAt: { x: number; y: number; id: number } | null = null;

	private boundPointerMove: (e: PointerEvent) => void;
	private boundPointerDown: (e: PointerEvent) => void;
	private boundPointerUp: (e: PointerEvent) => void;
	private boundKeyDown: (e: KeyboardEvent) => void;

	constructor(config: OpeningDragConfig) {
 this.validationConfig = config;
		this.parent = config.parent ?? config.scene;
		this.camera = config.camera;
		this.domElement = config.renderer.domElement;
		this.dimensions = config.dimensions;
		this.existingOpenings = config.existingOpenings;
		this.overrides = config.overrides ?? {};
		this.partitionSplit = config.partitionSplit ?? 0.5;
		this.onCommit = config.onCommit;
		this.onCancel = config.onCancel;
		this.onDragUpdate = config.onDragUpdate;
		// The height the engine draws the wall at, not an admin-rule re-read.
		this.wallHeightMm = Math.round(resolveWallHeightM(config.wallHeightM) * 1000);
		this.minCornerClearanceMm = getRuleValue<number>(
			"framing.minCornerClearanceMm",
			this.overrides,
		);

		this.boundPointerMove = this.handlePointerMove.bind(this);
		this.boundPointerDown = this.handlePointerDown.bind(this);
		this.boundPointerUp = this.handlePointerUp.bind(this);
		this.boundKeyDown = this.handleKeyDown.bind(this);
	}

	startPlaceDrag(widthMm: number, heightMm: number): void {
		this.mode = "place";
		this.startDrag(
			{
				id: nextOpeningId(),
				wall: "front",
				type: "window",
				xMm: 0,
				yMm: 0,
				widthMm,
				heightMm,
				glazed: true,
			},
			["front", "back", "left", "right"],
		);
	}

	startMoveDrag(opening: WallOpening): void {
		this.mode = "move";
		this.startDrag(opening, [opening.wall]);
	}

	cancelDrag(): void {
		this.cleanup();
		this.onDragUpdate?.(false, null);
		this.onCancel?.();
		emitter.emit("drag:cancel");
	}

	dispose(): void {
		this.cleanup();
		for (const wp of this.wallPlanes) {
			wp.plane.removeFromParent();
			wp.plane.geometry.dispose();
			(wp.plane.material as THREE.Material).dispose();
		}
		this.wallPlanes = [];
	}

	private startDrag(opening: WallOpening, allowedWalls: OpeningWallId[]) {
		this.activeOpening = { ...opening };
		this.allowedWalls = allowedWalls;
		this.currentWall = opening.wall;
		this.currentXMm = opening.xMm;
		this.currentYMm = opening.type === "door" ? 0 : opening.yMm;
		this.currentValid = true;
		this.isDragging = true;

		this.buildWallPlanes();
		this.createGhost(opening.widthMm, opening.heightMm);

		this.domElement.addEventListener("pointermove", this.boundPointerMove);
		this.domElement.addEventListener("pointerdown", this.boundPointerDown);
		this.domElement.addEventListener("pointerup", this.boundPointerUp);
		window.addEventListener("keydown", this.boundKeyDown);
		// Crosshair while picking a wall in place mode (no button held);
		// grabbing while actively dragging an existing handle.
		this.domElement.style.cursor =
			this.mode === "place" ? "crosshair" : "grabbing";

		emitter.emit("drag:start", {
			widthMm: opening.widthMm,
			heightMm: opening.heightMm,
		});
	}

	private buildWallPlanes(): void {
		for (const wp of this.wallPlanes) {
			wp.plane.removeFromParent();
			wp.plane.geometry.dispose();
			(wp.plane.material as THREE.Material).dispose();
		}

		const W = this.dimensions.width;
		const L = this.dimensions.length;
		const wallH = this.wallHeightMm / 1000;
		const wMm = Math.round(W * 1000);
		const lMm = Math.round(L * 1000);
		const dividerZ = getDividerZ(L, this.validationConfig.saunaPosition === "right" ? 1-this.partitionSplit : this.partitionSplit);
		const dividerLengthMm = getDividerLengthMm(this.dimensions);

		const planes: WallPlaneConfig[] = [];

		if (this.allowedWalls.includes("front")) {
			planes.push({
				id: "front",
				lengthMm: wMm,
				...this.makeFrontBackPlane(W, L, wallH, "front"),
			});
		}
		if (this.allowedWalls.includes("back")) {
			planes.push({
				id: "back",
				lengthMm: wMm,
				...this.makeFrontBackPlane(W, L, wallH, "back"),
			});
		}
		if (this.allowedWalls.includes("left")) {
			planes.push({
				id: "left",
				lengthMm: lMm,
				...this.makeSidePlane(W, L, wallH, "left"),
			});
		}
		if (this.allowedWalls.includes("right")) {
			planes.push({
				id: "right",
				lengthMm: lMm,
				...this.makeSidePlane(W, L, wallH, "right"),
			});
		}
		if (this.allowedWalls.includes("divider")) {
			planes.push({
				id: "divider",
				lengthMm: dividerLengthMm,
				...this.makeDividerPlane(W, wallH, dividerZ),
			});
		}

		for (const plane of planes) {
			plane.plane.visible = false;
			this.parent.add(plane.plane);
		}
		this.wallPlanes = planes;
	}

	private makeFrontBackPlane(
		width: number,
		length: number,
		wallH: number,
		wall: "front" | "back",
	) {
		const plane = new THREE.Mesh(
			new THREE.PlaneGeometry(width, wallH),
			// DoubleSide so the raycaster picks the wall the user actually
			// clicked, regardless of which side of the plane they're on.
			// FrontSide-only meant viewing the right wall from outside hit
			// the back face of the right plane (no intersection) and the
			// ray then continued and hit the *front* face of the left
			// plane — placing the window on the opposite wall.
			new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide }),
		);
		const z = wall === "front" ? length / 2 - 0.095 : -length / 2 + 0.095;
		plane.position.set(0, WALL_BASE_Y + wallH / 2, z);
		if (wall === "back") plane.rotation.y = Math.PI;

		return {
			plane,
			toWorld: (xMm: number, yMm: number) =>
				new THREE.Vector3(-width / 2 + xMm / 1000, WALL_BASE_Y + yMm / 1000, z),
			fromWorld: (point: THREE.Vector3) => ({
				xMm: Math.round((point.x + width / 2) * 1000),
				yMm: Math.round((point.y - WALL_BASE_Y) * 1000),
			}),
		};
	}

	private makeSidePlane(
		width: number,
		length: number,
		wallH: number,
		wall: "left" | "right",
	) {
		const plane = new THREE.Mesh(
			new THREE.PlaneGeometry(length, wallH),
			// DoubleSide so the raycaster picks the wall the user actually
			// clicked, regardless of which side of the plane they're on.
			// FrontSide-only meant viewing the right wall from outside hit
			// the back face of the right plane (no intersection) and the
			// ray then continued and hit the *front* face of the left
			// plane — placing the window on the opposite wall.
			new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide }),
		);
		const x = wall === "left" ? -width / 2 + 0.095 : width / 2 - 0.095;
		plane.position.set(x, WALL_BASE_Y + wallH / 2, 0);
		plane.rotation.y = wall === "left" ? Math.PI / 2 : -Math.PI / 2;

		return {
			plane,
			toWorld: (xMm: number, yMm: number) =>
				new THREE.Vector3(
					x,
					WALL_BASE_Y + yMm / 1000,
					-length / 2 + xMm / 1000,
				),
			fromWorld: (point: THREE.Vector3) => ({
				xMm: Math.round((point.z + length / 2) * 1000),
				yMm: Math.round((point.y - WALL_BASE_Y) * 1000),
			}),
		};
	}

	private makeDividerPlane(width: number, wallH: number, dividerZ: number) {
		const clearWidth = Math.max(
			0.4,
			width - (DIVIDER_EDGE_INSET_MM * 2) / 1000,
		);
		const plane = new THREE.Mesh(
			new THREE.PlaneGeometry(clearWidth, wallH),
			// DoubleSide so the raycaster picks the wall the user actually
			// clicked, regardless of which side of the plane they're on.
			// FrontSide-only meant viewing the right wall from outside hit
			// the back face of the right plane (no intersection) and the
			// ray then continued and hit the *front* face of the left
			// plane — placing the window on the opposite wall.
			new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide }),
		);
		plane.position.set(0, WALL_BASE_Y + wallH / 2, dividerZ - 0.01);

		return {
			plane,
			toWorld: (xMm: number, yMm: number) =>
				new THREE.Vector3(
					-width / 2 + xMm / 1000,
					WALL_BASE_Y + yMm / 1000,
					dividerZ - 0.01,
				),
			fromWorld: (point: THREE.Vector3) => ({
				xMm: Math.round(
					(point.x + width / 2) * 1000,
				),
				yMm: Math.round((point.y - WALL_BASE_Y) * 1000),
			}),
		};
	}

	private createGhost(widthMm: number, heightMm: number) {
		const widthM = widthMm / 1000;
		const heightM = heightMm / 1000;
		const ghost = new THREE.Group();
		const ghostMesh = new THREE.Mesh(
			new THREE.BoxGeometry(widthM, heightM, 0.05),
			MAT_VALID.clone(),
		);
		ghost.add(ghostMesh);
		ghost.visible = false;
		this.parent.add(ghost);
		this.ghostGroup = ghost;
		this.ghostMesh = ghostMesh;
	}

	private handlePointerMove(event: PointerEvent): void {
		if (
			!this.isDragging ||
			!this.activeOpening ||
			!this.ghostGroup ||
			!this.ghostMesh
		)
			return;

		// Place mode: a held button means OrbitControls is rotating
		// the camera, not the user picking a wall. Skip the ghost
		// update so the ghost doesn't snap around mid-orbit.
		if (this.mode === "place" && event.buttons !== 0) {
			return;
		}

		const rect = this.domElement.getBoundingClientRect();
		this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

		this.raycaster.setFromCamera(this.mouse, this.camera);
		const intersects = this.raycaster.intersectObjects(
			this.wallPlanes.map((item) => item.plane),
		);

		if (intersects.length === 0) {
			this.ghostGroup.visible = false;
			this.currentWall = null;
			this.currentValid = false;
			this.onDragUpdate?.(false, null);
			return;
		}

		const hit = intersects[0];
		const wallConfig = this.wallPlanes.find(
			(item) => item.plane === hit.object,
		);
		if (!wallConfig) return;

		// `toWorld`/`fromWorld` are named for the un-rotated case they were
		// written in; they are really parent-space. Convert the world-space
		// raycast hit into that space or every mapping is off by the rotation.
		const local = wallConfig.fromWorld(
			this.parent.worldToLocal(hit.point.clone()),
		);
		const heightMm = this.activeOpening.heightMm;
		const widthMm = this.activeOpening.widthMm;

		const xMm = local.xMm - widthMm / 2;
		const yMm =
			this.activeOpening.type === "door" ? 0 : local.yMm - heightMm / 2;

		const maxX = Math.max(
			this.minCornerClearanceMm,
			wallConfig.lengthMm - widthMm - this.minCornerClearanceMm,
		);
		const maxY = Math.max(0, this.wallHeightMm - heightMm);

		this.currentXMm = Math.round(
			Math.max(this.minCornerClearanceMm, Math.min(maxX, xMm)),
		);
		this.currentYMm = Math.round(Math.max(0, Math.min(maxY, yMm)));
		this.currentWall = wallConfig.id;
		this.currentValid = this.checkValidity(
			wallConfig.id,
			this.currentXMm,
			this.currentYMm,
		);

		const center = wallConfig.toWorld(
			this.currentXMm + widthMm / 2,
			this.currentYMm + heightMm / 2,
		);

		this.ghostGroup.position.copy(center);
		this.ghostGroup.visible = true;
		if (
			wallConfig.id === "front" ||
			wallConfig.id === "back" ||
			wallConfig.id === "divider"
		) {
			this.ghostGroup.rotation.set(0, 0, 0);
		} else {
			this.ghostGroup.rotation.set(0, Math.PI / 2, 0);
		}

		const material = this.ghostMesh.material as THREE.MeshBasicMaterial;
		material.color.setHex(this.currentValid ? 0x22c55e : 0xef4444);

		this.onDragUpdate?.(this.currentValid, wallConfig.id);
		emitter.emit("drag:move", {
			valid: this.currentValid,
			wall: wallConfig.id,
			xMm: this.currentXMm,
			yMm: this.currentYMm,
		});
	}

	private handlePointerDown(event: PointerEvent): void {
		// Track the down position so pointerup can tell the difference
		// between "user clicked to commit" (small delta) and "user
		// dragged to orbit the camera" (any meaningful movement).
		this.pointerDownAt = {
			x: event.clientX,
			y: event.clientY,
			id: event.pointerId,
		};
	}

	private handlePointerUp(event: PointerEvent): void {
		if (!this.isDragging || !this.activeOpening) return;

		// Place mode: only commit if the gesture was actually a click —
		// movement above the click threshold means OrbitControls just
		// finished a rotate/pan and we should *not* drop the window.
		// Move mode keeps its existing drag-to-release semantics.
		if (this.mode === "place") {
			const down = this.pointerDownAt;
			this.pointerDownAt = null;
			if (down && down.id === event.pointerId) {
				const dx = event.clientX - down.x;
				const dy = event.clientY - down.y;
				const CLICK_THRESHOLD_PX = 5;
				if (dx * dx + dy * dy > CLICK_THRESHOLD_PX * CLICK_THRESHOLD_PX) {
					// Was an orbit-drag, not a placement click.
					return;
				}
			}
		}

		if (!this.currentValid || !this.currentWall) {
			// Move mode releases must always end the gesture — if the user
			// dragged the handle off the wall plane and released there,
			// silently cancelling leaves a zombie controller alive and the
			// next click does nothing. Place mode keeps its early-return so
			// orbit-drag gestures stay in place mode for the next click.
			if (this.mode === "move") {
				this.cancelDrag();
			}
			return;
		}

		this.onCommit({
			...this.activeOpening,
			wall: this.currentWall,
			xMm: this.currentXMm,
			yMm: this.activeOpening.type === "door" ? 0 : this.currentYMm,
		});

		emitter.emit("drag:end", {
			placed: true,
			wall: this.currentWall,
			xMm: this.currentXMm,
			yMm: this.currentYMm,
		});
		this.cleanup();
	}

	private handleKeyDown(event: KeyboardEvent): void {
		if (event.key === "Escape") {
			this.cancelDrag();
		}
	}

	private checkValidity(
		wall: OpeningWallId,
		xMm: number,
		yMm: number,
	): boolean {
		const wallConfig = this.wallPlanes.find((item) => item.id === wall);
		if (!wallConfig || !this.activeOpening) return false;

    return validateOpeningSet({
      ...this.validationConfig,
      dimensions:this.dimensions,
      wallHeightMm:this.wallHeightMm,
      overrides:this.overrides,
      openings:[...this.existingOpenings.filter(o=>o.id!==this.activeOpening!.id),{...this.activeOpening,wall,xMm,yMm}],
    }).valid;
	}

	private cleanup(): void {
		this.isDragging = false;
		this.activeOpening = null;
		this.currentWall = null;
		this.pointerDownAt = null;
		this.domElement.removeEventListener("pointermove", this.boundPointerMove);
		this.domElement.removeEventListener("pointerdown", this.boundPointerDown);
		this.domElement.removeEventListener("pointerup", this.boundPointerUp);
		window.removeEventListener("keydown", this.boundKeyDown);
		this.domElement.style.cursor = "";

		if (this.ghostGroup) {
			this.ghostGroup.removeFromParent();
			this.ghostGroup = null;
			this.ghostMesh = null;
		}
	}
}
